import AppRouter from 'common/AppRouter';
import history from 'common/RouterHistory';
import {
  LOCAL_STORAGE_KEYS,
  SESSION_STORAGE_KEYS
} from 'common/constants/constants';
import { logFullStorySessionUrl } from 'common/services/tracking.service';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';
import {
  omitFromQsAndSaveInLocalStorage,
  omitFromQsAndSaveInSessionStorage
} from 'common/utils/utils';
import ErrorBoundary from 'components/ErrorBoundary';
import SnackBar from 'connected-components/SnackBar';
import Modal from 'modals';
import QueryString from 'query-string';
import { Component } from 'react';
import { Redirect, Route, Router, Switch } from 'react-router-dom';
import { CompatRouter } from 'react-router-dom-v5-compat';
import { RegisterV2 } from 'views/RegisterV2/RegisterV2';
import ChallengeCallback from 'views/challenge-callback/ChallengeCallback';
import EmailPreferences from 'views/email-preferences';
import LinkAccount from 'views/link-account';
import Login from 'views/login';
import OAuthHandler from 'views/oauth/OAuthHandler';
import Overdue from 'views/overdue';
import Root from 'views/root';
import SendInvitation from 'views/send-invitation';
import './App.scss';
import PersonalIdentificationForm from './views/RegisterV2/views/PersonalIdentificationForm';
import { LICENSE_PLAN_TIER_TYPE } from '@linearb/linjs-common';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { LBToaster } from '@linearb/buzz';

const queryClient = new QueryClient();

class App extends Component {
  componentDidMount() {
    const search = QueryString.parse(window.location.search, {
      ignoreQueryPrefix: true
    });
    const { license, authFlow, oid, t, authLevel } = search;

    if (t) {
      omitFromQsAndSaveInLocalStorage(
        LOCAL_STORAGE_KEYS.LINK_ACCOUNT_TOKEN,
        't'
      );
    }

    if (license && Object.values(LICENSE_PLAN_TIER_TYPE).includes(license)) {
      omitFromQsAndSaveInLocalStorage(LOCAL_STORAGE_KEYS.LICENSE, 'license');
    } else if (
      !BrowserStorageAccessor().local.get(LOCAL_STORAGE_KEYS.LICENSE)
    ) {
      // default to team license if not provided
      // we need to explicitly set this in local storage since a bunch of other code relies on it
      BrowserStorageAccessor().local.set(LOCAL_STORAGE_KEYS.LICENSE, 'team');
    }

    if (authFlow) {
      omitFromQsAndSaveInSessionStorage(
        SESSION_STORAGE_KEYS.AUTH_FLOW,
        'authFlow'
      );
    }

    if (authLevel) {
      omitFromQsAndSaveInSessionStorage(
        SESSION_STORAGE_KEYS.AUTH_LEVEL,
        'authLevel'
      );
    }

    if (oid) {
      omitFromQsAndSaveInSessionStorage(SESSION_STORAGE_KEYS.ORG_ID, 'oid');
    }

    // https://developer.fullstory.com/browser/get-session-details/
    window['_fs_ready'] = logFullStorySessionUrl;
  }

  render() {
    return (
      <div className='App'>
        <ErrorBoundary height='100vh' componentName='LinearB'>
          <QueryClientProvider client={queryClient}>
            <Router history={history}>
              <CompatRouter>
                <Switch>
                  <Route
                    path={AppRouter.LINK_ACCOUNT.path}
                    exact
                    component={LinkAccount}
                  />
                  <Route
                    path={AppRouter.OVERDUE.path}
                    exact
                    component={Overdue}
                  />
                  <Route path={AppRouter.LOGIN.path} exact component={Login} />
                  <Route
                    path={AppRouter.CHALLENGE_CALLBACK.path}
                    exact
                    component={ChallengeCallback}
                  />
                  <Route
                    path={AppRouter.SIGN_UP.path}
                    exact
                    component={RegisterV2}
                  />
                  <Route
                    path={AppRouter.PERSONAL_IDENTIFICATION_FORM.path}
                    exact
                    component={PersonalIdentificationForm}
                  />
                  <Route path={AppRouter.REGISTER.path} exact>
                    <Redirect to={AppRouter.SIGN_UP.path} />
                  </Route>
                  <Route
                    path={AppRouter.OAUTH_CALLBACK.path}
                    exact
                    component={OAuthHandler}
                  />
                  <Route
                    path={AppRouter.SEND_INVITATION.path}
                    component={SendInvitation}
                  />
                  <Route
                    path={AppRouter.EMAIL_PREFERENCES.path}
                    component={EmailPreferences}
                  />
                  <Route path={AppRouter.ROOT.path} component={Root} />
                </Switch>
                <Modal />
                <SnackBar />
              </CompatRouter>
            </Router>
            <LBToaster />
          </QueryClientProvider>
        </ErrorBoundary>
      </div>
    );
  }
}

export default App;
